import React, { useState, useCallback } from 'react';
import { Stack, Box, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Company, CompanyDocument } from '../../../../interfaces/users.interface';
import StandardDialog from '../../../Modals/StandardDialog';
import FileUploaderComp from '../../../Forms/FileUpload';
import { DataCell, DataRow, DataRowActions } from '../../../Tables/DataList';
import { tightTableSx } from '../../../Tables/dataTableStyles';
import { deleteCompanyDocument } from '../../../../services/companyDocuments';
import { getCompanyDocuments } from '../../../../services/companyDetail';
import { useCompanyResource } from '../../../Vendors/useCompanyResource';
import { TabEmpty, TabPagination, TabSkeletonRows } from '../../../Vendors/companyTabUi';

const PAGE_SIZE = 5;

type CompanyDocumentsTabProps = {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
  uploadOpen?: boolean;
  onUploadClose?: () => void;
};

export default function CompanyDocumentsTab(props: CompanyDocumentsTabProps) {
  const { company, userCanAdminCompany, uploadOpen: externalUploadOpen, onUploadClose } = props;
  const [internalUploadOpen, setInternalUploadOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { items: documents, count, loading, refetch } = useCompanyResource<CompanyDocument>(
    getCompanyDocuments,
    company.id,
    page,
    PAGE_SIZE,
  );

  const uploadOpen = externalUploadOpen !== undefined ? externalUploadOpen : internalUploadOpen;

  const handleUploadClose = useCallback(() => {
    if (onUploadClose) {
      onUploadClose();
    } else {
      setInternalUploadOpen(false);
    }
  }, [onUploadClose]);

  const handleDeleteDocument = useCallback(
    (doc: CompanyDocument) => {
      deleteCompanyDocument(doc).then(() => refetch());
    },
    [refetch],
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <StandardDialog title='Upload Document' handleClose={handleUploadClose} isOpen={uploadOpen}>
        <FileUploaderComp callback={() => { handleUploadClose(); refetch(); }} multiple={false} hideFileList={true} />
      </StandardDialog>

      <Box sx={tightTableSx}>
        <DataRow header>
          <DataCell xs={6}>Document Name</DataCell>
          <DataCell xs={4}>Description</DataCell>
          <DataCell xs={2}>&nbsp;</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={4} columns={[6, 4, 2]} />}

        {!loading && documents.length === 0 && <TabEmpty label='No documents' />}

        {!loading && documents.map((doc) => (
          <DataRow key={doc.id} onClick={() => {}} hover>
            <DataCell xs={6}>{doc.name}</DataCell>
            <DataCell xs={4}>Document</DataCell>
            <DataCell xs={2}>
              <DataRowActions>
                <a href={doc.document} target="_blank" rel="noreferrer">
                  <Tooltip title="View Document">
                    <IconButton size='small'>
                      <LinkOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </a>
                {userCanAdminCompany && (
                  <Tooltip title="Delete Document">
                    <IconButton size='small' onClick={() => handleDeleteDocument(doc)} disabled={!userCanAdminCompany}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </DataRowActions>
            </DataCell>
          </DataRow>
        ))}
      </Box>

      <TabPagination count={count} page={page} pageSize={PAGE_SIZE} onChange={setPage} />
    </Stack>
  );
}
